import { useCallback } from "react";
import { createUseStyles } from "react-jss";
import type { BoatSearchResult } from "../../../../common/src";
import { todayInTimeZone } from "../../../../common/src";
import { useAsyncData } from "../../hooks/useAsyncData";
import type { TripParams } from "../../routes";
import { applySearchDefaults, routeToHash } from "../../routes";
import type { VetBoatsTheme } from "../../theme";
import { trpc } from "../../trpc";
import { formatRange } from "../../utils/format";
import { Badge } from "../shared/Badge";
import { BoatCard, useBoatGridStyles } from "../shared/BoatCard";
import { Notice } from "../shared/Notice";
import { EmptyState, LoadError, Loading } from "../shared/StatusViews";
import { useLayoutStyles } from "../shared/styles";
import { QuickReserve } from "./QuickReserve";
import { SearchFilters } from "./SearchFilters";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  heading: { margin: "4px 0 12px", fontSize: 17 },
  meta: { color: theme.color.muted, fontSize: 14, margin: "-4px 0 12px" },
  problems: {
    margin: 0,
    paddingLeft: 18,
    fontSize: 13,
    color: theme.color.muted,
  },
  calendarLink: { fontSize: 14, fontWeight: 600 },
}));

type ResultEntry = BoatSearchResult["boats"][number];

const browserToday = (): string =>
  todayInTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);

export const FindBoatPage = ({ trip: requestedTrip }: { trip: TripParams }) => {
  // A fresh visit searches the default trip; the form and results always show the same search.
  const trip = applySearchDefaults(requestedTrip, browserToday());
  const classes = useStyles();
  const layout = useLayoutStyles();
  const grid = useBoatGridStyles();

  const { startDate, endDate, partySize } = trip;
  const dates = startDate && endDate ? { startDate, endDate } : undefined;

  const load = useCallback(
    (signal: AbortSignal) =>
      trpc.boat.search.query(
        { filters: { partySize }, startDate, endDate },
        { signal }
      ),
    [partySize, startDate, endDate]
  );
  const results = useAsyncData(load);

  const data = results.data;
  const personBlocked = (data?.personProblems.length ?? 0) > 0;
  const available = data?.boats.filter((b) => b.available !== false) ?? [];
  const unavailable = data?.boats.filter((b) => b.available === false) ?? [];

  const calendarHref = (boatId: number): string =>
    routeToHash({ page: "boat", boatId, trip });

  const renderCard = (entry: ResultEntry) => (
    <BoatCard
      key={entry.boat.id}
      boat={entry.boat}
      href={calendarHref(entry.boat.id)}
      dimmed={entry.available === false}
      badge={
        entry.available === true ? (
          <Badge tone="success">Available</Badge>
        ) : entry.available === false ? (
          <Badge tone="neutral">Unavailable</Badge>
        ) : undefined
      }
    >
      {entry.available === false && (
        <ul className={classes.problems}>
          {entry.problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {entry.available === true && dates && !personBlocked && (
        <QuickReserve
          boatId={entry.boat.id}
          dates={dates}
          partySize={partySize}
        />
      )}
      <div>
        <a className={classes.calendarLink} href={calendarHref(entry.boat.id)}>
          {dates ? "See calendar →" : "Check dates →"}
        </a>
      </div>
    </BoatCard>
  );

  return (
    <div className={layout.sections}>
      {/* Re-initialize the form whenever the URL's search changes (e.g. back button). */}
      <SearchFilters
        key={routeToHash({ page: "find", trip })}
        trip={trip}
        // Offer a reset only when the URL holds an explicit search.
        canReset={Object.values(requestedTrip).some((v) => v !== undefined)}
      />
      {results.error && (
        <LoadError message={results.error} onRetry={results.reload} />
      )}
      {results.loading && !data && <Loading label="Finding boats…" />}

      {data && (
        <section aria-live="polite">
          {data.personProblems.map((p) => (
            <Notice key={p} kind="info">
              {p}
            </Notice>
          ))}
          {data.hiddenByFilters > 0 && (
            <p className={classes.meta}>
              {data.hiddenByFilters} boat{data.hiddenByFilters === 1 ? "" : "s"}{" "}
              hidden by your filters.
            </p>
          )}
          {data.boats.length === 0 ? (
            <EmptyState title="No boats match">
              {data.hiddenByFilters > 0
                ? "Try a smaller party size."
                : "No boats are accepting reservations yet. Check back soon."}
            </EmptyState>
          ) : dates ? (
            <>
              <h2 className={classes.heading}>
                {available.length > 0
                  ? `Available ${formatRange(dates)}`
                  : `Nothing available ${formatRange(dates)}`}
              </h2>
              {available.length > 0 && (
                <div className={grid.grid}>{available.map(renderCard)}</div>
              )}
              {unavailable.length > 0 && (
                <>
                  <h2 className={classes.heading}>
                    Not available for these dates
                  </h2>
                  <div className={grid.grid}>{unavailable.map(renderCard)}</div>
                </>
              )}
            </>
          ) : (
            <>
              <p className={classes.meta}>
                Pick a boat to see its calendar, or enter dates to see which
                boats are free.
              </p>
              <div className={grid.grid}>{data.boats.map(renderCard)}</div>
            </>
          )}
        </section>
      )}
    </div>
  );
};
